/**
 * Metadata Configuration Component
 * Allows users to define metadata fields for document extraction and filtering.
 * Uses shadcn theme tokens for dark/light mode support.
 */

import React, { useState } from 'react'
import { Plus, Trash2, Info, Edit2, Check, X } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Switch } from '../../ui/switch'

export interface MetadataField {
  name: string
  type: 'text' | 'date' | 'select' | 'number' | 'boolean' | 'tags'
  description: string
  filterable: boolean
  include_in_chunk?: boolean
  options?: string[]
  example_values?: string[]
}

export interface MetadataSchema {
  fields: MetadataField[]
}

interface MetadataConfigProps {
  value: MetadataSchema
  onChange: (schema: MetadataSchema) => void
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'tags', label: 'Tags' },
]

export const MetadataConfig: React.FC<MetadataConfigProps> = ({ value, onChange }) => {
  const [isAdding, setIsAdding] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [newField, setNewField] = useState<MetadataField>({
    name: '', type: 'text', description: '', filterable: true, include_in_chunk: false, options: [],
  })
  const [editField, setEditField] = useState<MetadataField>({
    name: '', type: 'text', description: '', filterable: true, include_in_chunk: false, options: [],
  })

  const handleAddField = () => {
    if (!newField.name.trim()) return
    onChange({ ...value, fields: [...(value.fields || []), { ...newField }] })
    setNewField({ name: '', type: 'text', description: '', filterable: true, include_in_chunk: false, options: [] })
    setIsAdding(false)
  }

  const handleRemoveField = (index: number) => {
    onChange({ ...value, fields: value.fields.filter((_, i) => i !== index) })
  }

  const handleStartEdit = (index: number) => {
    setEditingIndex(index)
    setEditField({ ...fields[index] })
  }

  const handleCancelEdit = () => {
    setEditingIndex(null)
  }

  const handleSaveEdit = () => {
    if (!editField.name.trim()) return
    onChange({ ...value, fields: value.fields.map((f, i) => i === editingIndex ? { ...editField } : f) })
    setEditingIndex(null)
  }

  const fields = value.fields || []

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Metadata Fields</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Define metadata to extract from training documents for better search filtering
        </p>
      </div>

      {/* Collapsible info hint */}
      <details className="group rounded-lg border bg-muted/50 text-sm">
        <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none text-muted-foreground hover:text-foreground">
          <Info className="w-4 h-4 shrink-0" />
          <span className="font-medium">How metadata works</span>
        </summary>
        <div className="px-3 pb-3 pt-0 text-xs text-muted-foreground space-y-1.5">
          <p>1. Define fields here (e.g., speaker_name, category, date)</p>
          <p>2. AI extracts these fields automatically when you upload training data</p>
          <p>3. Your chatbot uses them to filter and find more relevant answers</p>
          <p className="text-foreground/70"><strong>Example:</strong> Add "speaker_name" so users can ask "What did John say?" and get filtered results.</p>
        </div>
      </details>

      {/* Fields list */}
      {fields.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Type</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground hidden sm:table-cell">Description</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground hidden md:table-cell">Filterable</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground hidden md:table-cell whitespace-nowrap">In Chunk</th>
                <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {fields.map((field, index) => (
                <tr key={index} className="bg-card">
                  {editingIndex === index ? (
                    <>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={editField.name}
                          onChange={e => setEditField({ ...editField, name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={editField.type}
                          onChange={e => setEditField({ ...editField, type: e.target.value as any })}
                          className="w-full px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell">
                        <input
                          type="text"
                          value={editField.description}
                          onChange={e => setEditField({ ...editField, description: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <Switch checked={editField.filterable} onCheckedChange={v => setEditField({ ...editField, filterable: v })} />
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <Switch checked={editField.include_in_chunk || false} onCheckedChange={v => setEditField({ ...editField, include_in_chunk: v })} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={handleSaveEdit} className="p-1 rounded hover:bg-muted text-green-600"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={handleCancelEdit} className="p-1 rounded hover:bg-muted text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2.5 text-sm font-medium text-foreground">{field.name}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{field.type}</span>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-muted-foreground hidden sm:table-cell">{field.description || '—'}</td>
                      <td className="px-3 py-2.5 text-sm hidden md:table-cell">
                        {field.filterable
                          ? <span className="text-green-600 dark:text-green-400 text-xs font-medium">Yes</span>
                          : <span className="text-muted-foreground text-xs">No</span>
                        }
                      </td>
                      <td className="px-3 py-2.5 text-sm hidden md:table-cell">
                        {field.include_in_chunk
                          ? <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">Yes</span>
                          : <span className="text-muted-foreground text-xs">Auto</span>
                        }
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleStartEdit(index)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleRemoveField(index)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add field form */}
      {isAdding ? (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium text-foreground">Add Metadata Field</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={newField.name}
              onChange={e => setNewField({ ...newField, name: e.target.value })}
              placeholder="Field name (e.g., speaker_name)"
            />
            <select
              value={newField.type}
              onChange={e => setNewField({ ...newField, type: e.target.value as any })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <Input
            value={newField.description}
            onChange={e => setNewField({ ...newField, description: e.target.value })}
            placeholder="Description (e.g., Name of the person speaking)"
          />
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-5">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={newField.filterable} onCheckedChange={v => setNewField({ ...newField, filterable: v })} />
                Filterable
              </label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={newField.include_in_chunk || false} onCheckedChange={v => setNewField({ ...newField, include_in_chunk: v })} />
                Include in Chunk
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setIsAdding(false); setNewField({ name: '', type: 'text', description: '', filterable: true, include_in_chunk: false, options: [] }) }}>Cancel</Button>
              <Button size="sm" onClick={handleAddField} disabled={!newField.name.trim()}>Add</Button>
            </div>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add Metadata Field
        </Button>
      )}
    </div>
  )
}
